import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  ArrowBack,
  AccountBalanceWallet,
  AttachMoney,
  Close,
  MovieCreation,
  OpenInNew,
  PhotoLibrary,
  Save,
  Search,
  TokenOutlined,
  TrendingUp,
} from "@mui/icons-material";
import moment from "moment";
import { GlobalContext } from "../../context/GlobalContext";
import UpdateCredit from "./components/UpdateCredit";

const BRAND = "#6D28D9";

function parsePlan(plan) {
  if (!plan) return null;
  if (typeof plan === "object") return plan;
  try {
    return JSON.parse(plan);
  } catch {
    return null;
  }
}

function initials(name, email) {
  const s = String(name || email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function shortLabel(item) {
  const raw = String(item?.feature || item?.model || item?.job_id || "Item").trim();
  return raw.length > 28 ? `${raw.slice(0, 28)}…` : raw;
}

function StatCard({ icon, label, value, hint, color = BRAND }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: alpha(color, 0.12), color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} noWrap>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" color="text.disabled">
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function MediaDetailModal({ open, item, onClose }) {
  if (!item) return null;
  const isVideo = item.type === "video";
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        {shortLabel(item)}
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#0b0b0b",
            mb: 2,
            minHeight: 200,
            display: "grid",
            placeItems: "center",
          }}
        >
          {item.media_url && isVideo ? (
            <Box
              component="video"
              src={item.media_url}
              controls
              playsInline
              sx={{ width: "100%", maxHeight: 440, display: "block" }}
            />
          ) : item.media_url ? (
            <Box
              component="img"
              src={item.media_url}
              alt=""
              sx={{ width: "100%", maxHeight: 440, objectFit: "contain", display: "block" }}
            />
          ) : (
            <Typography color="grey.500" sx={{ py: 6 }}>
              No media available
            </Typography>
          )}
        </Box>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={(item.type || "media").toUpperCase()} sx={{ bgcolor: BRAND, color: "#fff", fontWeight: 800 }} />
            <Chip size="small" label={item.status || "—"} />
            <Typography variant="caption" color="text.secondary">
              {item.created_at ? moment(item.created_at).format("D MMM YYYY, HH:mm") : "—"}
            </Typography>
          </Stack>
          {[
            ["Job ID", item.job_id],
            ["Feature", item.feature],
            ["Model", item.model],
            ["Credits", item.credits != null ? String(item.credits) : null],
            ["Name", item.name],
          ]
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <Stack key={label} direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                  {value}
                </Typography>
              </Stack>
            ))}
          {item.prompt ? (
            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.75 }}>
                Prompt
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                  border: "1px solid",
                  borderColor: "divider",
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                {item.prompt}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {item.media_url ? (
          <Button
            href={item.media_url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNew />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Open media
          </Button>
        ) : null}
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function goBackToUsers() {
  const params = new URLSearchParams(window.location.search);
  params.set("page", "users");
  params.delete("uid");
  const next = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({ path: next }, "", next);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const UserProfile = ({ lang }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const [uid, setUid] = React.useState(
    () => new URLSearchParams(window.location.search).get("uid") || "",
  );

  React.useEffect(() => {
    const sync = () => setUid(new URLSearchParams(window.location.search).get("uid") || "");
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const [loading, setLoading] = React.useState(true);
  const [detail, setDetail] = React.useState(null);
  const [tab, setTab] = React.useState("images");
  const [saving, setSaving] = React.useState(false);
  const [creditOpen, setCreditOpen] = React.useState(false);
  const [preview, setPreview] = React.useState(null);

  const [media, setMedia] = React.useState([]);
  const [mediaTotal, setMediaTotal] = React.useState(0);
  const [mediaPage, setMediaPage] = React.useState(0);
  const [mediaRows, setMediaRows] = React.useState(12);
  const [mediaLoading, setMediaLoading] = React.useState(false);

  const [usage, setUsage] = React.useState([]);
  const [usageTotal, setUsageTotal] = React.useState(0);
  const [usagePage, setUsagePage] = React.useState(0);
  const [usageRows, setUsageRows] = React.useState(25);
  const [usageLoading, setUsageLoading] = React.useState(false);
  const [usageStatus, setUsageStatus] = React.useState("all");
  const [usageTask, setUsageTask] = React.useState("all");
  const [usageQInput, setUsageQInput] = React.useState("");
  const [usageQ, setUsageQ] = React.useState("");
  const [taskOptions, setTaskOptions] = React.useState([]);

  const [form, setForm] = React.useState({
    name: "",
    email: "",
    status: "active",
    credits: "",
    newPassword: "",
  });

  const load = React.useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await hitAxios({
        path: `/api/admin/get_user_profile/${encodeURIComponent(uid)}`,
        post: false,
        admin: true,
        showLoading: false,
      });
      if (res?.data?.success) {
        setDetail(res.data.data);
        const u = res.data.data.user || {};
        setForm({
          name: u.name || "",
          email: u.email || "",
          status: String(u.status || "active").toLowerCase(),
          credits: String(u.credits ?? ""),
          newPassword: "",
        });
      } else {
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios, uid]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setUsageQ(usageQInput);
      setUsagePage(0);
    }, 350);
    return () => window.clearTimeout(t);
  }, [usageQInput]);

  const mediaType = tab === "videos" ? "videos" : "images";

  const loadMedia = React.useCallback(async () => {
    if (!uid || (tab !== "images" && tab !== "videos")) return;
    setMediaLoading(true);
    try {
      const params = new URLSearchParams({
        type: mediaType,
        page: String(mediaPage + 1),
        limit: String(mediaRows),
      });
      const res = await hitAxios({
        path: `/api/admin/get_user_media/${encodeURIComponent(uid)}?${params}`,
        post: false,
        admin: true,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        setMedia(res.data.data.items || []);
        setMediaTotal(Number(res.data.data.total || 0));
      }
    } finally {
      setMediaLoading(false);
    }
  }, [hitAxios, uid, tab, mediaType, mediaPage, mediaRows]);

  React.useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const loadUsage = React.useCallback(async () => {
    if (!uid || tab !== "usage") return;
    setUsageLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(usagePage + 1),
        limit: String(usageRows),
        status: usageStatus,
        task: usageTask,
      });
      if (usageQ.trim()) params.set("q", usageQ.trim());
      const res = await hitAxios({
        path: `/api/admin/get_user_usage/${encodeURIComponent(uid)}?${params}`,
        post: false,
        admin: true,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        setUsage(res.data.data.items || []);
        setUsageTotal(Number(res.data.data.total || 0));
        setTaskOptions(res.data.data.task_options || []);
      }
    } finally {
      setUsageLoading(false);
    }
  }, [hitAxios, uid, tab, usagePage, usageRows, usageStatus, usageTask, usageQ]);

  React.useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await hitAxios({
        path: "/api/admin/update_user",
        post: true,
        admin: true,
        obj: {
          uid,
          name: form.name,
          email: form.email,
          status: form.status,
          credits: form.credits,
          newPassword: form.newPassword || undefined,
        },
      });
      if (res?.data?.success) {
        setForm((f) => ({ ...f, newPassword: "" }));
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  if (!uid) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">No user selected.</Typography>
        <Button onClick={goBackToUsers} sx={{ mt: 2, textTransform: "none" }}>
          Back to Users
        </Button>
      </Box>
    );
  }

  if (loading && !detail) {
    return (
      <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error">User not found.</Typography>
        <Button onClick={goBackToUsers} sx={{ mt: 2, textTransform: "none" }}>
          Back to Users
        </Button>
      </Box>
    );
  }

  const user = detail.user || {};
  const cards = detail.cards || {};
  const plan = parsePlan(user.plan);
  const planLabel = plan?.title || plan?.name || "Free";
  const orders = detail.orders || [];
  const imageCount = detail.stats?.images?.total ?? cards.image_generations ?? 0;
  const videoCount = detail.stats?.videos?.total ?? cards.video_generations ?? 0;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={goBackToUsers}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Users
          </Button>
          <Divider orientation="vertical" flexItem />
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: alpha(BRAND, 0.15),
              color: BRAND,
              fontWeight: 800,
            }}
          >
            {initials(user.name, user.email)}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {user.name || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email} · {planLabel}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => setCreditOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Adjust Credits
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={saving}
            onClick={saveProfile}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: BRAND,
              "&:hover": { bgcolor: alpha(BRAND, 0.9) },
            }}
          >
            Save Changes
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<AccountBalanceWallet />}
            label="Wallet Balance"
            value={Number(cards.wallet_balance || 0).toLocaleString()}
            hint="Credits remaining"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<TokenOutlined />}
            label="Credits Used"
            value={Number(cards.credits_used || 0).toLocaleString()}
            hint={`Refunded: ${Number(cards.credits_refunded || 0).toLocaleString()}`}
            color="#7C3AED"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<AttachMoney />}
            label="Total Spent"
            value={money(cards.total_spent)}
            hint={`This month: ${money(cards.spent_this_month)}`}
            color="#059669"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<PhotoLibrary />}
            label="Images"
            value={Number(cards.image_generations || 0).toLocaleString()}
            hint={`${detail.stats?.images?.active ?? 0} active`}
            color="#2563EB"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<MovieCreation />}
            label="Videos"
            value={Number(cards.video_generations || 0).toLocaleString()}
            hint={`${detail.stats?.videos?.completed ?? 0} completed`}
            color="#EA580C"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<TrendingUp />}
            label="Gens This Month"
            value={Number(cards.generations_this_month || 0).toLocaleString()}
            hint="Successful usage events"
            color="#DC2626"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
              Personal Information
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                fullWidth
              />
              <TextField size="small" label="UID" value={user.uid || ""} fullWidth InputProps={{ readOnly: true }} />
              <TextField
                size="small"
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Credits"
                value={form.credits}
                onChange={(e) => setForm((f) => ({ ...f, credits: e.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="New password"
                type="password"
                value={form.newPassword}
                onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
                fullWidth
                helperText="Leave blank to keep current password"
              />
              <Divider />
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Plan
                </Typography>
                <Chip size="small" label={planLabel} sx={{ alignSelf: "flex-start", fontWeight: 700 }} />
                <Typography variant="caption" color="text.secondary">
                  Plan ends:{" "}
                  {user.planEnding || user.plan_ending
                    ? moment(user.planEnding || user.plan_ending).format("D MMM YYYY")
                    : "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Joined: {user.createdAt ? moment(user.createdAt).format("D MMM YYYY, HH:mm") : "—"}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "divider", mt: 2 }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
              Credits by Task
            </Typography>
            {(detail.task_breakdown || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No usage yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {detail.task_breakdown.map((t) => (
                  <Stack key={t.task} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: "60%" }}>
                      {t.task}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {Number(t.total_credits || 0).toLocaleString()} · {t.count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setMediaPage(0);
                setUsagePage(0);
              }}
              sx={{
                px: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
                "& .Mui-selected": { color: BRAND },
                "& .MuiTabs-indicator": { bgcolor: BRAND },
              }}
            >
              <Tab value="images" label={`Images (${imageCount})`} />
              <Tab value="videos" label={`Videos (${videoCount})`} />
              <Tab value="usage" label={`Usage`} />
              <Tab value="orders" label={`Orders (${orders.length})`} />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {(tab === "images" || tab === "videos") && (
                <>
                  {mediaLoading && media.length === 0 ? (
                    <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : media.length === 0 ? (
                    <Typography color="text.secondary">
                      No {tab === "videos" ? "videos" : "images"} yet.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.5}>
                      {media.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.job_id}>
                          <Paper
                            variant="outlined"
                            onClick={() => setPreview(item)}
                            sx={{
                              overflow: "hidden",
                              borderRadius: 2,
                              cursor: "pointer",
                              transition: "box-shadow .15s, transform .15s",
                              "&:hover": {
                                boxShadow: 3,
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                height: 168,
                                bgcolor: "#111",
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              {item.media_url && tab === "videos" ? (
                                <Box
                                  component="video"
                                  src={item.media_url}
                                  muted
                                  preload="metadata"
                                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : item.media_url ? (
                                <Box
                                  component="img"
                                  src={item.media_url}
                                  alt=""
                                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <Typography variant="caption" color="grey.500">
                                  No preview
                                </Typography>
                              )}
                              <Chip
                                size="small"
                                label={item.status || "—"}
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  left: 8,
                                  bgcolor: "rgba(0,0,0,0.65)",
                                  color: "#fff",
                                  fontWeight: 700,
                                  height: 22,
                                }}
                              />
                            </Box>
                            <Box sx={{ px: 1.25, py: 1 }}>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {shortLabel(item)} ·{" "}
                                {item.created_at ? moment(item.created_at).fromNow() : "—"}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  <TablePagination
                    component="div"
                    count={mediaTotal}
                    page={mediaPage}
                    onPageChange={(_, p) => setMediaPage(p)}
                    rowsPerPage={mediaRows}
                    onRowsPerPageChange={(e) => {
                      setMediaRows(parseInt(e.target.value, 10));
                      setMediaPage(0);
                    }}
                    rowsPerPageOptions={[8, 12, 24, 48]}
                  />
                </>
              )}

              {tab === "usage" && (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    sx={{ mb: 2 }}
                    alignItems={{ sm: "center" }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search task or notes"
                      value={usageQInput}
                      onChange={(e) => setUsageQInput(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: { sm: 220 } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={usageStatus}
                        onChange={(e) => {
                          setUsageStatus(e.target.value);
                          setUsagePage(0);
                        }}
                      >
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                        <MenuItem value="error">Error</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Task</InputLabel>
                      <Select
                        label="Task"
                        value={usageTask}
                        onChange={(e) => {
                          setUsageTask(e.target.value);
                          setUsagePage(0);
                        }}
                      >
                        <MenuItem value="all">All tasks</MenuItem>
                        {taskOptions.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Task</TableCell>
                        <TableCell>Credits</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>When</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usageLoading && usage.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>Loading…</TableCell>
                        </TableRow>
                      ) : usage.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>No usage logs.</TableCell>
                        </TableRow>
                      ) : (
                        usage.map((ul) => (
                          <TableRow key={ul.id} hover>
                            <TableCell>{ul.task || "—"}</TableCell>
                            <TableCell>{ul.credits ?? "—"}</TableCell>
                            <TableCell>
                              <Chip size="small" label={ul.status || "—"} />
                            </TableCell>
                            <TableCell>
                              {ul.created_at ? moment(ul.created_at).format("D MMM YYYY HH:mm") : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={usageTotal}
                    page={usagePage}
                    onPageChange={(_, p) => setUsagePage(p)}
                    rowsPerPage={usageRows}
                    onRowsPerPageChange={(e) => {
                      setUsageRows(parseInt(e.target.value, 10));
                      setUsagePage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )}

              {tab === "orders" && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No orders.</TableCell>
                      </TableRow>
                    ) : (
                      orders.map((o) => (
                        <TableRow key={o.id}>
                          <TableCell>#{o.id}</TableCell>
                          <TableCell>{money(o.amount)}</TableCell>
                          <TableCell>{o.gateway || "—"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={o.status || "—"} />
                          </TableCell>
                          <TableCell>
                            {o.created_at ? moment(o.created_at).format("D MMM YYYY HH:mm") : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <MediaDetailModal open={Boolean(preview)} item={preview} onClose={() => setPreview(null)} />

      {creditOpen ? (
        <UpdateCredit
          forceOpen
          onClose={() => setCreditOpen(false)}
          lang={lang}
          hitAxios={hitAxios}
          hangleGetUsers={async () => {
            await load();
            setCreditOpen(false);
          }}
          params={{ row: user, value: user.credits }}
        />
      ) : null}
    </Box>
  );
};

export default UserProfile;
